import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { HeroEntity } from '@generated/gql';
import { continueToChamberRoute } from '@helpers/data-helper';
import { SubgraphService } from '@services/graph/subgraph.service';
import { SoundService } from '@services/sound.service';
import { StorageService } from '@services/storage.service';
import { LoadingSmallComponent } from '@shared/components/loading-small/loading-small.component';
import { MAIN_ROUTES } from '@shared/constants/routes.constant';
import { SACRA_GAME_SELECTED_HERO } from '@shared/constants/storage-keys.constant';
import { filter, finalize } from 'rxjs';

import { ROUTE_WITH_HEADER_BUTTONS } from './constants/header-buttons.constant';

@Component({
  selector: 'app-header-buttons',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, LoadingSmallComponent],
  templateUrl: './header-buttons.component.html',
  styleUrl: './header-buttons.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'g-flex-column',
  },
})
export class HeaderButtonsComponent implements OnInit {
  public readonly noHeader = input<boolean>(false);
  public readonly account = input.required<string>();
  public readonly chainId = input.required<number>();
  protected readonly isLoading = signal<boolean>(true);

  public readonly MAIN_ROUTES = MAIN_ROUTES;

  private readonly router = inject(Router);
  private readonly soundService = inject(SoundService);
  private readonly subgraphService = inject(SubgraphService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly heroes = signal<HeroEntity[]>([]);
  protected readonly selectedHero = signal<HeroEntity | null>(null);
  private readonly localStorage = inject(StorageService);

  public readonly isShowButtons = signal(false);

  ngOnInit(): void {
    this.checkButtons(this.router.url);
    this.loadHeroes();

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(event => {
      this.checkButtons((event as NavigationEnd).url);
    });
  }

  getAdventureRoute(): string[] {
    this.soundService.play({ key: 'dungeon_enter' });

    if (!this.selectedHero()) {
      return [];
    }

    return continueToChamberRoute(
      this.selectedHero()?.dungeon?.currentChamber?.chamber?.isBattle ?? false,
      this.selectedHero()?.dungeon?.currentChamber?.chamber?.isEvent ?? false,
      this.selectedHero()!.meta.id,
      this.selectedHero()!.heroId,
      this.selectedHero()?.dungeon?.id ?? 'no_dung',
      this.selectedHero()?.dungeon?.currentChamber?.chamber?.id ?? 'no_chamber',
      this.selectedHero()?.dungeon?.currentChamberIndex ?? 0,
      this.selectedHero()?.dungeon?.currentChamber?.iteration ?? 0,
    );
  }

  private checkButtons(url: string) {
    const cleanUrl = url.indexOf('?') >= 0 ? url.substring(0, url.indexOf('?')) : url;

    this.isShowButtons.set(
      ROUTE_WITH_HEADER_BUTTONS.some(it => cleanUrl.includes(`/${it}/`) || cleanUrl.includes(`/${it}`)),
    );
  }

  private loadHeroes() {
    this.heroes.set([]);

    this.subgraphService
      .user$(this.account())
      .pipe(
        filter(userInfo => !!userInfo),
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(userInfo => {
        const heroes: HeroEntity[] = userInfo!.heroes.map(hero => {
          return hero as HeroEntity;
        });

        this.heroes.set(heroes);

        this.selectHero();
      });
  }

  private selectHero() {
    if (this.localStorage.get(SACRA_GAME_SELECTED_HERO)) {
      const key = JSON.parse(this.localStorage.get(SACRA_GAME_SELECTED_HERO));

      const found = this.heroes().find(hero => `${this.account()}-${this.chainId()}-${hero.id}` === key);

      if (found) {
        this.selectedHero.set(found);
      } else {
        this.selectedHero.set(this.heroes()[0]);
      }
    } else {
      this.selectedHero.set(this.heroes()[0]);
    }

    this.isLoading.set(false);
  }
}
